import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuth";
import { portalApi, apiError } from "@/lib/portalApi";
import { toast } from "sonner";

export default function PortalLogin() {
  const { user, login } = usePortalAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [fEmail, setFEmail] = useState("");
  const [fSending, setFSending] = useState(false);
  const [fSent, setFSent] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  if (user) return <Navigate to={loc.state?.from?.pathname || "/portal"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate(loc.state?.from?.pathname || "/portal", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally { setLoading(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setFSending(true);
    try {
      await portalApi.post("/auth/forgot-password", { email: fEmail, origin: window.location.origin });
      setFSent(true);
      toast.success("If that email is in our cohort, a reset link is on its way.");
    } catch (err) { toast.error(apiError(err)); } finally { setFSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#2A2825] text-[#F4EFE6] grain relative flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <p className="tracking-[0.4em] uppercase text-xs text-[#E8B486] mb-4 text-center">Outpoured Portal</p>
        <h1 className="font-serif-display font-light text-4xl md:text-5xl text-center mb-2">Welcome back.</h1>
        <p className="text-center text-[#C9BEB0] font-body mb-10">Sign in to your cohort space.</p>
        <form onSubmit={submit} className="space-y-5" data-testid="portal-login-form">
          <div>
            <label className="block text-xs tracking-overline uppercase text-[#C9BEB0] mb-2">Email</label>
            <input data-testid="portal-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-[#5a534b] focus:border-[#E8B486] rounded-md px-4 py-3 text-[#F4EFE6] outline-none transition-colors" />
          </div>
          <div>
            <label className="block text-xs tracking-overline uppercase text-[#C9BEB0] mb-2">Password</label>
            <input data-testid="portal-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border border-[#5a534b] focus:border-[#E8B486] rounded-md px-4 py-3 text-[#F4EFE6] outline-none transition-colors" />
          </div>
          {error && <p data-testid="portal-login-error" className="text-[#E8A08a] text-sm font-body">{error}</p>}
          <button type="submit" disabled={loading} data-testid="portal-login-submit"
            className="w-full rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] py-3.5 text-sm tracking-wide transition-colors duration-500 disabled:opacity-50">
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgot(true); setFEmail(email); setFSent(false); }}
              className="text-sm text-[#C9BEB0] hover:text-[#E8B486] transition-colors">Forgot your password?</button>
          </div>
        </form>
        <p className="text-center text-xs text-[#8a7a6a] font-body mt-8">Trouble signing in? Email dej@outpouredliving.com</p>
      </div>

      {forgot && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setForgot(false)}>
          <div data-testid="forgot-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-6 md:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {fSent ? (
              <div className="text-center" data-testid="forgot-sent">
                <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-2">Check your inbox</p>
                <h3 className="font-serif-display text-2xl mb-3">A reset link is on its way.</h3>
                <p className="text-[#5E4B3C] font-body text-sm mb-6">If that email is part of our cohort, you'll receive a link to choose a new password. It expires in one hour.</p>
                <button onClick={() => setForgot(false)} className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors">Done</button>
              </div>
            ) : (
              <form onSubmit={sendReset}>
                <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-2">Reset password</p>
                <h3 className="font-serif-display text-2xl mb-3">No worries — we'll help.</h3>
                <p className="text-[#5E4B3C] font-body text-sm mb-5">Enter your email and we'll send you a link to set a new password.</p>
                <input data-testid="forgot-email" type="email" required value={fEmail} onChange={(e) => setFEmail(e.target.value)} placeholder="you@example.com"
                  className="w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 font-body outline-none focus:border-[#A9724E] mb-4" />
                <div className="flex items-center justify-end gap-3">
                  <button type="button" onClick={() => setForgot(false)} className="text-sm text-[#8a7a6a]">Cancel</button>
                  <button type="submit" data-testid="forgot-send" disabled={fSending || !fEmail}
                    className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors disabled:opacity-50">
                    {fSending ? "Sending…" : "Send reset link"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
